import { Button, Card, DatePicker, Form, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Activity = { id: string; code: string; name: string };

type MeetingRow = {
  id: string;
  activity_id: string;
  activity_code?: string;
  activity_name?: string;
  meeting_date: string;
  starts_at?: string | null;
  venue_label?: string | null;
  status: string;
  marked_count?: number;
  present_count?: number;
  absent_count?: number;
};

export default function ActivityMeetingsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const activityId = params.get('activity_id') || undefined;
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.activity.manage') ||
      user?.permissions?.includes('education.admin')
  );

  const activities = useQuery({
    queryKey: ['education-activities-for-meetings'],
    queryFn: async () => {
      const res = await api.get<Activity[]>('/education/activities');
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const meetings = useQuery({
    queryKey: ['education-activity-meetings', activityId],
    queryFn: async () => {
      const res = await api.get<MeetingRow[]>('/education/activity-meetings', {
        params: { activity_id: activityId },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const createMeeting = useMutation({
    mutationFn: (values: { meeting_date: string }) =>
      api.post('/education/activity-meetings', {
        activity_id: activityId,
        meeting_date: values.meeting_date,
        seed_roster: true,
      }),
    onSuccess: (result) => {
      const body = result.data as { id?: string; seeded?: number };
      message.success(t('activityMeetingCreated', { seeded: body?.seeded ?? 0 }));
      queryClient.invalidateQueries({ queryKey: ['education-activity-meetings'] });
      if (body?.id) navigate(`/education/activities/meetings/${body.id}`);
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('activityMeetings')}
      description={t('activityMeetingsIntro')}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/activities')}>{t('backToActivities')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline">
            <Form.Item label={t('activity')}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ minWidth: 260 }}
                value={activityId}
                placeholder={t('selectActivity')}
                options={(activities.data || []).map((a) => ({
                  value: a.id,
                  label: `${a.code} — ${a.name}`,
                }))}
                onChange={(value) => {
                  const next = new URLSearchParams(params);
                  if (value) next.set('activity_id', value);
                  else next.delete('activity_id');
                  setParams(next);
                }}
              />
            </Form.Item>
          </Form>
        </Card>

        {canManage && activityId ? (
          <Card size="small" title={t('newActivityMeeting')}>
            <Form
              layout="inline"
              onFinish={(values: { meeting_date: dayjs.Dayjs }) =>
                createMeeting.mutate({ meeting_date: values.meeting_date.format('YYYY-MM-DD') })
              }
            >
              <Form.Item
                name="meeting_date"
                label={t('meetingDate')}
                rules={[{ required: true }]}
                initialValue={dayjs()}
              >
                <DatePicker />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={createMeeting.isPending}>
                {t('createMeetingWithRoster')}
              </Button>
            </Form>
          </Card>
        ) : null}

        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={meetings.isLoading}
            dataSource={meetings.data || []}
            pagination={{ pageSize: 25 }}
            columns={[
              {
                title: t('activity'),
                render: (_: unknown, row: MeetingRow) =>
                  `${row.activity_code || ''} — ${row.activity_name || ''}`,
              },
              { title: t('meetingDate'), dataIndex: 'meeting_date', width: 120 },
              { title: t('venue'), dataIndex: 'venue_label', width: 140 },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 120,
                render: (v: string) => <Tag>{v}</Tag>,
              },
              {
                title: t('attendanceSummary'),
                width: 140,
                render: (_: unknown, row: MeetingRow) =>
                  `${row.present_count ?? 0}P / ${row.absent_count ?? 0}A / ${row.marked_count ?? 0}`,
              },
              {
                title: t('actions'),
                width: 120,
                render: (_: unknown, row: MeetingRow) => (
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => navigate(`/education/activities/meetings/${row.id}`)}
                  >
                    {t('open')}
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
